import React, { useState } from 'react';
import { Clock, Users, DollarSign, Printer, Factory, Truck, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { printLabourReport } from '../lib/labourReportPrint';

const h = (n) => (n === null || n === undefined ? '—' : `${Number(n) % 1 === 0 ? Number(n) : Number(n).toFixed(1)}h`);
const people = (n) => `${n} ${n === 1 ? 'person' : 'people'}`;
const money = (v) => `$${Number(v || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }) : '');
const OT_LABEL = { all_ot: 'All OT', all_reg: 'All reg', all_dt: '2× DT' };

const Tile = ({ icon: Icon, label, value, sub, tone }) => (
  <div className={`rounded-lg border p-3 ${tone}`}>
    <div className="flex items-center gap-2"><Icon className="w-4 h-4 opacity-70" /><p className="text-xs text-slate-500">{label}</p></div>
    <p className="text-xl font-bold text-slate-900 mt-0.5">{value}</p>
    {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
  </div>
);

const Row = ({ r, total, showCost, isField, source }) => {
  const [open, setOpen] = useState(false);
  const pct = total > 0 ? Math.round((r.total_hours / total) * 100) : 0;
  const key = `${source}-${(r.employee_id || r.employee_name).replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen((v) => !v)} data-testid={`labour-row-${key}`}>
        <td className="py-2 pl-3 pr-2">
          <div className="flex items-center gap-2">
            {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-500" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-500" />}
            <span className="font-medium text-slate-800">{r.employee_name}</span>
            {r.contractor && <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-300 text-slate-500">contractor</span>}
            {showCost && r.rate_set === false && !r.contractor && <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">no pay rate</span>}
          </div>
        </td>
        <td className="py-2 px-2 text-right text-slate-700">{h(r.regular_hours)}</td>
        <td className="py-2 px-2 text-right text-slate-700">{r.overtime_hours > 0 ? <span className="text-amber-700 font-medium">{h(r.overtime_hours)}</span> : '—'}</td>
        {isField && <td className="py-2 px-2 text-right text-slate-700">{r.double_time_hours > 0 ? <span className="text-rose-700 font-medium">{h(r.double_time_hours)}</span> : '—'}</td>}
        {isField && <td className="py-2 px-2 text-right text-slate-700">{r.travel_hours > 0 ? <span className="text-sky-700">{h(r.travel_hours)}</span> : '—'}</td>}
        <td className="py-2 px-2 text-right font-bold text-slate-900">{h(r.total_hours)}</td>
        <td className="py-2 px-2 text-right text-slate-500">{r.days_worked}</td>
        <td className="py-2 px-2 text-right">
          <div className="flex items-center justify-end gap-2"><div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-blue-600 rounded-full" style={{ width: `${pct}%` }} /></div><span className="text-xs text-slate-500 w-8 text-right">{pct}%</span></div>
        </td>
        {showCost && <td className="py-2 px-3 text-right text-slate-800">{r.contractor ? '—' : money(r.loaded_cost)}</td>}
      </tr>
      {open && (
        <tr className="bg-slate-50/70"><td colSpan={showCost ? (isField ? 9 : 7) : (isField ? 8 : 6)} className="px-3 pb-3 pt-1">
          {(r.entries || []).length === 0 ? <p className="text-xs text-slate-500">No daily detail</p> : (
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-1.5">
              {r.entries.map((e, i) => (
                <div key={i} className="text-xs bg-white border border-slate-200 rounded px-2 py-1 flex items-center justify-between gap-2">
                  <span className="text-slate-600">{fmtDate(e.date)}</span>
                  <span className="font-semibold text-slate-900">{h(e.hours ?? ((e.regular_hours || 0) + (e.overtime_hours || 0)))}
                    {e.overtime_hours > 0 && <span className="text-amber-700 font-normal"> · {h(e.overtime_hours)} OT</span>}
                    {e.double_time_hours > 0 && <span className="text-rose-700 font-normal"> · DT</span>}
                    {e.travel_hours > 0 && <span className="text-sky-700 font-normal"> · {h(e.travel_hours)} trv</span>}
                    {!isField && e.status === 'pending' && <span className="text-purple-700 font-normal"> · pending</span>}
                    {isField && OT_LABEL[e.ot_mode] && e.ot_mode !== '8_ot' && e.ot_mode !== 'all_dt' && <span className="text-slate-500 font-normal"> · {OT_LABEL[e.ot_mode]}</span>}
                  </span>
                </div>
              ))}
            </div>
          )}
        </td></tr>
      )}
    </>
  );
};

const Section = ({ title, icon: Icon, rows, hours, cost, range, showCost, isField, source, onPrint, accent }) => (
  <div className="bg-white/70 rounded-lg border border-slate-200 overflow-hidden" data-testid={`labour-section-${source}`}>
    <div className={`px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3 ${accent}`}>
      <Icon className="w-4 h-4 text-slate-700" />
      <h4 className="font-semibold text-slate-900">{title}</h4>
      <span className="text-sm text-slate-600">{h(hours)} · {rows.length} {rows.length === 1 ? 'person' : 'people'}{range ? ` · ${fmtDate(range.from)} → ${fmtDate(range.to)}` : ''}</span>
      {showCost && <span className="text-sm font-semibold text-slate-800">{money(cost)}</span>}
      <button onClick={onPrint} disabled={!rows.length} data-testid={`labour-report-${source}-btn`}
        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 text-slate-700">
        <Printer className="w-3.5 h-3.5" /> {title.split(' ')[0]} report
      </button>
    </div>
    {rows.length === 0 ? <p className="px-4 py-6 text-sm text-slate-500">No {title.toLowerCase()} logged on this work order yet.</p> : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-200">
            <th className="text-left font-semibold py-2 pl-3 pr-2">Employee</th><th className="text-right font-semibold py-2 px-2">Reg</th><th className="text-right font-semibold py-2 px-2">OT</th>
            {isField && <th className="text-right font-semibold py-2 px-2">DT</th>}{isField && <th className="text-right font-semibold py-2 px-2">Travel</th>}
            <th className="text-right font-semibold py-2 px-2">Total</th><th className="text-right font-semibold py-2 px-2">Days</th><th className="text-right font-semibold py-2 px-2">Share</th>
            {showCost && <th className="text-right font-semibold py-2 px-3">Loaded cost</th>}
          </tr></thead>
          <tbody>{rows.map((r) => <Row key={`${r.employee_id}-${r.employee_name}`} r={r} total={hours} showCost={showCost} isField={isField} source={source} />)}</tbody>
        </table>
      </div>
    )}
  </div>
);

export const ProjectLabourTab = ({ data, project, projectNumber }) => {
  if (!data) return <div className="bg-white/60 rounded-lg border border-slate-200 p-10 text-center text-slate-500" data-testid="labour-loading">Loading shop & field hours…</div>;
  const rows = data.employees || [];
  const shop = rows.filter((r) => r.source === 'shop');
  const field = rows.filter((r) => r.source === 'field');
  const showCost = !!data.cost_visible && rows.some((r) => r.loaded_cost !== undefined);
  const print = (scope) => { if (!printLabourReport({ data, project, projectNumber, scope })) toast.error('Pop-up blocked — allow pop-ups to print the report'); };

  return (
    <div className="bg-white/60 backdrop-blur-xl rounded-lg border border-slate-200" data-testid="project-labour-tab">
      <div className="p-5 border-b border-slate-200 flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h3 className="text-lg font-semibold text-slate-800">Team & Labour Hours</h3>
          <p className="text-xs text-slate-500">Live from the Shop Tracker and Field Tracker — {h(data.total_hours)} across {data.employee_count} {data.employee_count === 1 ? 'person' : 'people'}</p>
        </div>
        <button onClick={() => print('both')} disabled={!rows.length} data-testid="labour-report-both-btn"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-lg text-sm font-medium">
          <Printer className="w-4 h-4" /> Generate labour report
        </button>
      </div>
      {data.error && <p className="px-5 py-3 text-sm text-rose-700 bg-rose-50 border-b border-rose-200" data-testid="labour-error">{data.error}</p>}
      {(data.on_clock_now || []).length > 0 && (
        <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-200 text-sm text-emerald-900 flex flex-wrap items-center gap-2" data-testid="labour-on-clock">
          <Clock className="w-4 h-4" />
          <b>On the clock now ({data.on_clock_now.length}):</b>
          {data.on_clock_now.map((p, i) => (
            <span key={i} className="px-2 py-0.5 rounded-full bg-white border border-emerald-200 text-xs">
              {p.employee_name}{p.running_hours != null ? ` · ${p.running_hours}h so far` : ''}
            </span>
          ))}
          <span className="text-xs text-emerald-800 w-full">Their hours land here when they clock out — pending (not yet approved) hours are already counted.</span>
        </div>
      )}
      {rows.length === 0 ? (
        <div className="p-10 text-center" data-testid="labour-empty">
          <Users className="h-10 w-10 text-slate-500 mx-auto mb-3" />
          <p className="font-medium text-slate-600">No shop or field hours on this work order yet</p>
          <p className="text-sm text-slate-500 mt-1">
            {(data.on_clock_now || []).length > 0
              ? `${data.on_clock_now.length} ${data.on_clock_now.length === 1 ? 'person is' : 'people are'} clocked in on ${projectNumber} right now — the first hours show up at clock-out. Approval isn't needed for hours to appear.`
              : `Hours appear here as soon as someone clocks onto ${projectNumber} in the shop or a field crew is clocked in on it — before approval too.`}
          </p>
        </div>
      ) : (
        <div className="p-5 space-y-5">
          <div className={`grid grid-cols-2 ${showCost ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3`}>
            <Tile icon={Clock} label="Total hours" value={h(data.total_hours)} sub={people(data.employee_count)} tone="bg-blue-50 border-blue-100" />
            <Tile icon={Factory} label="Shop" value={h(data.shop_hours)} sub={people(data.shop_employee_count ?? shop.length)} tone="bg-slate-50 border-slate-200" />
            <Tile icon={Truck} label="Field" value={h(data.field_hours)} sub={people(data.field_employee_count ?? field.length)} tone="bg-emerald-50 border-emerald-100" />
            {showCost && <Tile icon={DollarSign} label="Loaded labour cost" value={money(data.total_labour_cost)} sub={`${money(data.labour_wage_cost)} wages + ${money(data.labour_burden_cost)} burden`} tone="bg-amber-50 border-amber-100" />}
          </div>
          <Section title="Shop labour" icon={Factory} rows={shop} hours={data.shop_hours} cost={data.shop_labour_cost} range={data.shop_date_range} showCost={showCost} isField={false} source="shop" onPrint={() => print('shop')} accent="bg-slate-50" />
          <Section title="Field labour" icon={Truck} rows={field} hours={data.field_hours} cost={data.field_labour_cost} range={data.field_date_range} showCost={showCost} isField source="field" onPrint={() => print('field')} accent="bg-emerald-50/60" />
          {showCost && data.rate_missing?.length > 0 && (
            <p className="text-xs text-amber-700" data-testid="labour-rate-missing-note">{data.rate_missing.length} {data.rate_missing.length === 1 ? 'person has' : 'people have'} no pay rate set ({data.rate_missing.join(', ')}) — costed at the default wage.</p>
          )}
        </div>
      )}
    </div>
  );
};
